// Runtime unit instance.

import type { ChampionDef, AbilityId } from "../data/champions";
import type { EnemyDef } from "../data/enemies";
import type { HexCoord } from "../engine/hex/HexCoord";
import type { ItemId } from "../data/items";

export type Team = "player" | "enemy";

export interface UnitKindDef {
  displayName: string;
  baseHp: number;
  baseAd: number;
  baseAp: number;
  range: number;
  moveSpeed: number;
  baseAttackSpeed: number; // > 0 = unit can auto-attack each turn
  abilityId: AbilityId | null;
  abilityCooldown: number; // turns between ability casts
  iconUrl: string;
  critChance?: number;     // fraction of autos that crit (1.75× damage)
}

export interface Unit {
  id: string;
  team: Team;
  def: UnitKindDef;
  level: number;
  stars: number;        // 1, 2, or 3 — for display only; stat scaling already baked in
  currentHp: number;
  pos: HexCoord;
  championDef?: ChampionDef;
  enemyDef?: EnemyDef;
  // Combat state — reset at start of each combat.
  damageDealt: number;                  // total effective damage output this combat
  cooldownTurns: number;                // turns remaining until next ability cast (0 = ready)
  autoAttackCount: number;              // total autos fired this combat (for on-hit effects)
  stunTurnsRemaining: number;           // unit cannot act while > 0
  stasisTurnsRemaining: number;         // unit untargetable & frozen while > 0
  sleepTurnsRemaining: number;          // like stun, but removed on any incoming damage
  shieldAmount: number;                 // absorbs incoming damage before HP
  damageReductionPercent: number;       // 0..1 flat damage reduction while active
  damageReductionTurnsRemaining: number;
  olafUltTurnsRemaining: number;        // Olaf Ragnarök: CC immune + bonus AS/AD/lifesteal while > 0
  alphaStrikeTurnsRemaining: number;    // Master Yi: damage immune while > 0 (Alpha Strike)
  tibbersUnitId: string | null;         // Annie: ID of active Tibbers — blocks re-cast while alive
  jaxCounterStrikeTurnsRemaining: number; // Jax Counter Strike: dodges incoming autos while > 0; slams on expiry
  jaxCounterStrikeBlockedCount: number;   // autos blocked this stance; +20% slam damage per block
  abilityCastCount: number;             // how many times this unit has cast its ability this combat (for stacking effects)
  passiveStacks: number;                // generic stack counter (Darius bleed, Draven axes, Aurelion Sol star power…)
  bleedTickDamage: number;              // physical DoT applied each turn end if > 0
  bleedTurnsRemaining: number;          // turns of bleed remaining
  jayceHammerForm: boolean;             // Jayce: true once he enters melee hammer form
  currentTarget: Unit | null;           // locked target — only re-evaluated when dead or null
  // Buff camp rewards
  redBuff: boolean;
  blueBuff: boolean;
  // Yuumi aura multiplier on the bonded ally — 1 = no aura; >1 = bonded (scales with Yuumi's stars).
  // Applied to AD, AP, and max HP in effectiveStats.
  yuumiBoostMult: number;
  // Item system
  item: ItemId | null;
  bansheeActive: boolean;               // Banshee's Veil: true until the first enemy ability is blocked
  healReductionPercent: number;         // 0..1 — reduces heals received (set by Executioner's on hit)
  healReductionTurnsRemaining: number;  // turns until healReductionPercent resets to 0
  warmogRegenAccum: number;             // unused in turn-based (warmog heals per-turn in engine)
  vayneStacks: number;                  // Vayne Silver Bolts: stacks on this unit; procs at 3
}

let idCounter = 0;
export const nextUnitId = (prefix = "u"): string => `${prefix}_${idCounter++}`;

export const resetUnitIds = (): void => {
  idCounter = 0;
};
